import { type CookieName, getCookie, removeCookieValue, setCookieValue } from "./cookie.utils";

const listeners = new Set<() => void>();

const cookieSnapshotCache = new Map<CookieName, { serialized: string; snapshot: object | undefined }>();

function notify(): void {
  for (const fn of listeners) {
    fn();
  }
}

/**
 * Subscribe to cookie mutations performed through this store’s {@link setCookie} / {@link removeCookie}.
 * Used with `useSyncExternalStore` ([docs](https://react.dev/reference/react/useSyncExternalStore)).
 */
export function subscribeCookieStore(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/**
 * Immutable snapshot of one cookie for `useSyncExternalStore` (cached when the value is unchanged).
 */
export function getCookieSnapshot<T>(name: CookieName): T | undefined {
  const value = getCookie<T>(name);
  const serialized = value ? JSON.stringify(value) : undefined;
  const hit = cookieSnapshotCache.get(name);
  if (hit && hit.serialized === serialized) return hit.snapshot as T | undefined;
  cookieSnapshotCache.set(name, { serialized, snapshot: value ? JSON.parse(serialized) : undefined });
  return value;
}

/** Set cookie with app defaults and notify subscribers (e.g. {@link useCookie}). */
export function setCookie<T = object>(name: CookieName, value: T, overrides?: Cookies.CookieAttributes): void {
  setCookieValue(name, value, overrides);
  notify();
}

/** Remove cookie with app defaults and notify subscribers. */
export function removeCookie<T = object>(name: CookieName, overrides?: Cookies.CookieAttributes): void {
  removeCookieValue(name, overrides);
  notify();
}
